import React, { useEffect, useState, Fragment } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { FormattedMessage, useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';
import {
    TextField, RadioGroup,
    FormControlLabel,
    Radio,
    FormHelperText,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import GridContainer from '../../../components/Grid/GridContainer';
import GridItem from '../../../components/Grid/GridItem';
import Button from '../../../components/Button/CustomButton';
import Typography from '../../../components/Typography/CustomTypography';
import Snackbar from '../../../components/Snackbar/Snackbar';
import Loader from '../../../components/Loader/Loader';
import { APIErrors, SETTINGS_DISPLAY_PRODUCT_ITEM } from '../../../common/Constants';
import Validation from '../../../common/Validation';

import styles from '../../../assets/jss/pages/adminSettingBoothStyle';
import CommonFunctions from '../../../common/Functions';

const useStyles = makeStyles(styles);

const SettingBoothPage = ({
    settingBoothData, errors, isCompleted, getSettingBooth, updateSettingBooth, getSettingBoothClear,
    updateSettingBoothClear, isCompletedGet, isLoading, isSuccessful, eventInfo, fetchInfoEvent,
}) => {
    const [settings, setSettings] = useState({});
    const [dialog, setDialog] = useState({ isOpen: false, type: 'success', message: '' });
    const [validateResults, setValidateResults] = useState({});
    const intl = useIntl();
    const { shortName } = useParams();

    useEffect(() => {
        if (shortName) getSettingBooth(shortName);
    }, [getSettingBooth, shortName]);

    useEffect(() => {
        if (isCompletedGet) {
            setSettings(settingBoothData);
            getSettingBoothClear();
        }
    }, [isCompletedGet, settingBoothData, getSettingBoothClear]);

    useEffect(() => {
        if (isCompleted && errors.code !== APIErrors.badRequest) {
            fetchInfoEvent(shortName);
            setDialog({
                isOpen: true,
                type: isSuccessful ? 'success' : 'error',
                message: isSuccessful
                    ? intl.formatMessage({ id: 'apiSuccess.default' })
                    : errors.message || intl.formatMessage({ id: 'apiErrors.default' }),
            });
            updateSettingBoothClear();
        }
    }, [isCompleted, isSuccessful, errors, updateSettingBoothClear, intl]);

    const handleChangeInput = (zone, field, value) => {
        setSettings({ ...settings, [zone]: { ...settings[zone], [field]: value } });
    };
    const handleChangeAll = (value) => {
        setSettings({ ...settings, ads_settings: value });
    };

    const handleChangeRadio = (zone, field, value) => {
        setSettings({
            ...settings,
            [zone]: {
                ...settings[zone],
                settings_display_info_product: {
                    ...settings[zone].settings_display_info_product, [field]: value,
                },
            },
        });
    };

    const handleCloseErrorDialog = (e, reason) => {
        if (reason === 'clickaway') return;
        setDialog({ ...dialog, isOpen: false });
    };

    const handleSubmit = () => {
        if (validate(settings)) {
            const formattedParams = CommonFunctions.trimObject({
                small_zone: eventInfo.small_zone ? settings.small_zone : undefined,
                standard_zone: eventInfo.standard_zone ? settings.standard_zone : undefined,
                big_zone: eventInfo.big_zone ? settings.big_zone : undefined,
                grand_zone: eventInfo.grand_zone ? settings.grand_zone : undefined,
                // add Ads
                register_ads: settings.ads_settings.register_ads,
                register_ads_max: settings.ads_settings.register_ads_max,
                price_ads: settings.ads_settings.price_ads,
                total_ads: settings.ads_settings.total_ads,
            });
            updateSettingBooth(shortName, formattedParams);
        }
    };

    const validate = (values) => {
        const validateRules = {
            ...(eventInfo.small_zone ? zoneValidationRules('small_zone', values.small_zone) : {}),
            ...(eventInfo.standard_zone ? zoneValidationRules('standard_zone', values.standard_zone) : {}),
            ...(eventInfo.big_zone ? zoneValidationRules('big_zone', values.big_zone) : {}),
            ...(eventInfo.grand_zone ? zoneValidationRules('grand_zone', values.grand_zone) : {}),
            // add Ads
            ...(adsValidationRules(values.ads_settings)),
        };
        const validateResult = Validation.validateAllRules(validateRules);

        if (Object.keys(validateResult).length > 0) {
            setValidateResults({ ...validateResult });
            return false;
        }
        setValidateResults({});
        return true;
    };

    const zoneValidationRules = (zone, values) => ({
        [`${zone}_name`]: [
            () => Validation.isNotEmpty(values.name),
            () => Validation.validateMaxLength(values.name, 30, 'string'),
        ],
        [`${zone}_name_en`]: [
            () => !_.isEmpty(eventInfo.languages) && Validation.isNotEmpty(values.name_en),
            () => !_.isEmpty(eventInfo.languages) && Validation.validateMaxLength(values.name_en, 30, 'string'),
            () => !_.isEmpty(eventInfo.languages) && Validation.validateLatin(values.name_en),
        ],
        [`${zone}_audio`]: intValidationRules(values.audio),
        [`${zone}_image`]: intValidationRules(values.image),
        [`${zone}_video`]: intValidationRules(values.video),
        [`${zone}_total_media`]: totalValidationRules(values.total_media, values.image, values.audio),
        [`${zone}_price`]: [
            () => Validation.isNotEmpty(values.price),
            () => Validation.isNumber(values.price),
            () => Validation.isNonNegative(values.price),
        ],
    });
    const adsValidationRules = (values) => ({
        register_ads: [() => Validation.isNotEmpty(values.register_ads)],
        total_ads: [
            () => Validation.isNotEmpty(values.total_ads),
            () => Validation.isInt(values.total_ads),
            () => Validation.isNonNegative(values.total_ads),
        ],
        register_ads_max: [
            () => Validation.isNotEmpty(values.register_ads_max),
            () => Validation.isInt(values.register_ads_max),
            () => Validation.isNonNegative(values.register_ads_max),
            values.register_ads ? () => Validation.validateBigWithSmall(values.total_ads, values.register_ads_max) : () => { },
        ],
        price_ads: [
            values.register_ads ? () => Validation.isNotEmpty(values.price_ads) : () => { },
            values.register_ads ? () => Validation.isNumber(values.price_ads) : () => { },
            values.register_ads ? () => Validation.isNonNegative(values.price_ads) : () => { },
        ],
    });

    const intValidationRules = (value) => ([
        () => Validation.isNotEmpty(value),
        () => Validation.isInt(value),
        () => Validation.isNonNegative(value),
    ]);

    const totalValidationRules = (total, image, audio) => ([
        ...intValidationRules(total),
        () => {
            if (total >= image && total >= audio) {
                return { result: 'success' };
            }
            return {
                result: 'error',
                message: 'validation.totalTooSmall',
            };
        },
    ]);

    return (
        <>
            <GridContainer>
                <GridItem lg={4} md={6}>
                    <SettingBooth
                        title={intl.formatMessage({ id: 'adminSettingBooth.smallZone' })}
                        zone="small_zone"
                        settings={settings.small_zone}
                        handleChangeInput={handleChangeInput}
                        validateResults={validateResults}
                        disabled={!eventInfo.small_zone}
                        handleChangeRadio={handleChangeRadio}
                        eventInfo={eventInfo}
                    />
                </GridItem>
                <GridItem lg={4} md={6}>
                    <SettingBooth
                        title={intl.formatMessage({ id: 'adminSettingBooth.standardZone' })}
                        zone="standard_zone"
                        settings={settings.standard_zone}
                        handleChangeInput={handleChangeInput}
                        validateResults={validateResults}
                        disabled={!eventInfo.standard_zone}
                        handleChangeRadio={handleChangeRadio}
                        eventInfo={eventInfo}
                    />
                </GridItem>
                <GridItem lg={4} md={6}>
                    <SettingBooth
                        title={intl.formatMessage({ id: 'adminSettingBooth.bigZone' })}
                        zone="big_zone"
                        settings={settings.big_zone}
                        handleChangeInput={handleChangeInput}
                        validateResults={validateResults}
                        disabled={!eventInfo.big_zone}
                        handleChangeRadio={handleChangeRadio}
                        eventInfo={eventInfo}
                    />

                </GridItem>
                <GridItem lg={8} md={12}>
                    <SettingBooth
                        title={intl.formatMessage({ id: 'adminSettingBooth.grandZone' })}
                        zone="grand_zone"
                        settings={settings.grand_zone}
                        handleChangeInput={handleChangeInput}
                        validateResults={validateResults}
                        disabled={!eventInfo.grand_zone}
                        handleChangeRadio={handleChangeRadio}
                        eventInfo={eventInfo}
                    />

                </GridItem>
                <GridItem lg={12} md={12}>
                    <SettingAds
                        // title={intl.formatMessage({ id: 'adminSettingBooth.bigZone' })}
                        title="広告募集"
                        zone="ads_settings"
                        settings={settings.ads_settings}
                        handleChangeInput={handleChangeInput}
                        validateResults={validateResults}
                        handleChangeAll={handleChangeAll}
                        disabled={!eventInfo.display_ads}
                    />
                </GridItem>
                {/* add Ads */}

                <GridItem md={12} style={{ textAlign: 'center', marginTop: 20 }}>
                    <Button
                        color="info"
                        style={{ width: '200px', fontSize: '20px' }}
                        size="md"
                        gradient
                        onClick={handleSubmit}
                    >
                        {intl.formatMessage({ id: 'apply' })}
                    </Button>
                </GridItem>
                <Snackbar
                    open={dialog.isOpen}
                    onClose={handleCloseErrorDialog}
                    message={dialog.message}
                    severity={dialog.type}
                />
            </GridContainer>
            <Loader isLoading={isLoading} />
        </>
    );
};

const SettingBooth = ({
    title, zone, settings, handleChangeInput, validateResults, disabled, handleChangeRadio, eventInfo,
}) => {
    const classes = useStyles();
    const intl = useIntl();

    const numberInputProps = {
        min: 0,
    };
    const gridColWidth = {
        first: 5,
        second: 7,
    };

    const FILTER_SETTINGS_DISPLAY_PRODUCT_ITEM = zone !== 'grand_zone'
        ? SETTINGS_DISPLAY_PRODUCT_ITEM
        : {
            display_purchase_link: SETTINGS_DISPLAY_PRODUCT_ITEM.display_purchase_link,
            display_has_outside_link: SETTINGS_DISPLAY_PRODUCT_ITEM.display_has_outside_link,
            display_business_contact: SETTINGS_DISPLAY_PRODUCT_ITEM.display_business_contact,
        };

    const handleKeyPressInput = (e) => {
        if (!/\d/.test(e.key)) e.preventDefault();
    };

    const changeIntInput = (field, value) => {
        if (/^\d*$/.test(value)) {
            if (handleChangeInput) {
                handleChangeInput(zone, field, value ? parseInt(value, 10) : '');
            }
        }
    };
    const changeName = (e) => handleChangeInput && handleChangeInput(zone, 'name', e.target.value);
    const changeNameEN = (e) => handleChangeInput && handleChangeInput(zone, 'name_en', e.target.value);
    const changeTotalMedia = (e) => changeIntInput('total_media', e.target.value);
    const changeAudio = (e) => changeIntInput('audio', e.target.value);
    const changeVideo = (e) => changeIntInput('video', e.target.value);
    const changeImage = (e) => changeIntInput('image', e.target.value);
    // const changeAdsPrice = (e) => changeIntInput('price_ads', e.target.value);
    const changePrice = (e) => changeIntInput('price', e.target.value);

    const nameMessageID = _.get(validateResults, `${zone}_name.message`);
    const nameMessageENID = _.get(validateResults, `${zone}_name_en.message`);
    const totalMediaMessageID = _.get(validateResults, `${zone}_total_media.message`);
    const audioMessageID = _.get(validateResults, `${zone}_audio.message`);
    const videoMessageID = _.get(validateResults, `${zone}_video.message`);
    const imageMessageID = _.get(validateResults, `${zone}_image.message`);
    // const adsPriceMessageID = _.get(validateResults, `${zone}_price_ads.message`);
    const priceMessageID = _.get(validateResults, `${zone}_price.message`);

    const handleChangeDisplay = (e, field) => {
        if (handleChangeRadio) {
            handleChangeRadio(zone, field, e.target.value === 'true');
        }
    };

    return (
        <fieldset className={classes.booth + (disabled ? ` ${classes.disabled}` : '')} disabled={disabled}>
            <legend>
                <Typography variant="h5" color="primary" bold>{title}</Typography>
            </legend>
            <GridContainer spacing={2}>
                <GridItem md={gridColWidth.first}>
                    <FormattedMessage id="adminSettingBooth.boothName" />
                </GridItem>
                <GridItem md={gridColWidth.second}>
                    <TextField
                        variant="outlined"
                        className={classes.input}
                        value={settings.name}
                        onChange={changeName}
                        error={!!_.get(validateResults, `${zone}_name`)}
                        helperText={nameMessageID ? intl.formatMessage({ id: nameMessageID }, { maxLength: 30 }) : ''}
                    />
                </GridItem>
                {!_.isEmpty(eventInfo.languages) && (
                    <GridItem md={gridColWidth.first}>
                        <FormattedMessage id="adminSettingBooth.boothNameEN" />
                    </GridItem>
                )}
                {!_.isEmpty(eventInfo.languages) && (
                    <GridItem md={gridColWidth.second}>
                        <TextField
                            variant="outlined"
                            className={classes.input}
                            value={settings.name_en}
                            onChange={changeNameEN}
                            error={!!_.get(validateResults, `${zone}_name_en`)}
                            helperText={nameMessageENID ? intl.formatMessage({ id: nameMessageENID }, { maxLength: 30 }) : ''}
                        />
                    </GridItem>
                )}
                <GridItem md={gridColWidth.first}>
                    <FormattedMessage id="adminSettingBooth.totalImageAudio" />
                </GridItem>
                <GridItem md={gridColWidth.second}>
                    <TextField
                        variant="outlined"
                        type="number"
                        className={classes.input}
                        value={settings.total_media}
                        onChange={changeTotalMedia}
                        onKeyPress={handleKeyPressInput}
                        inputProps={numberInputProps}
                        InputProps={{ classes: { root: classes.numberInput } }}
                        error={!!_.get(validateResults, `${zone}_total_media`)}
                        helperText={totalMediaMessageID ? intl.formatMessage({ id: totalMediaMessageID }) : ''}
                    />
                </GridItem>
                <GridItem md={gridColWidth.first}>
                    <FormattedMessage id="image" />
                </GridItem>
                <GridItem md={gridColWidth.second}>
                    <TextField
                        variant="outlined"
                        type="number"
                        className={classes.input}
                        value={settings.image}
                        onChange={changeImage}
                        onKeyPress={handleKeyPressInput}
                        inputProps={numberInputProps}
                        InputProps={{ classes: { root: classes.numberInput } }}
                        error={!!_.get(validateResults, `${zone}_image`)}
                        helperText={imageMessageID ? intl.formatMessage({ id: imageMessageID }) : ''}
                    />
                </GridItem>
                <GridItem md={gridColWidth.first}>
                    <FormattedMessage id="audio" />
                </GridItem>
                <GridItem md={gridColWidth.second}>
                    <TextField
                        variant="outlined"
                        type="number"
                        className={classes.input}
                        value={settings.audio}
                        onChange={changeAudio}
                        onKeyPress={handleKeyPressInput}
                        inputProps={numberInputProps}
                        InputProps={{ classes: { root: classes.numberInput } }}
                        error={!!_.get(validateResults, `${zone}_audio`)}
                        helperText={audioMessageID ? intl.formatMessage({ id: audioMessageID }) : ''}
                    />
                </GridItem>
                <GridItem md={gridColWidth.first}>
                    <FormattedMessage id="video" />
                </GridItem>
                <GridItem md={gridColWidth.second}>
                    <TextField
                        variant="outlined"
                        type="number"
                        className={classes.input}
                        value={settings.video}
                        onChange={changeVideo}
                        onKeyPress={handleKeyPressInput}
                        inputProps={numberInputProps}
                        InputProps={{ classes: { root: classes.numberInput } }}
                        error={!!_.get(validateResults, `${zone}_video`)}
                        helperText={videoMessageID ? intl.formatMessage({ id: videoMessageID }) : ''}
                    />
                </GridItem>
                <GridItem md={gridColWidth.first}>
                    <FormattedMessage id="price" />
                </GridItem>
                <GridItem md={gridColWidth.second}>
                    <TextField
                        variant="outlined"
                        className={classes.input}
                        value={settings.price}
                        onChange={changePrice}
                        onKeyPress={handleKeyPressInput}
                        InputProps={{ classes: { root: `${classes.numberInput} ${classes.price}` } }}
                        error={!!_.get(validateResults, `${zone}_price`)}
                        helperText={priceMessageID ? intl.formatMessage({ id: priceMessageID }) : ''}
                    />
                </GridItem>
                {
                    Object.keys(FILTER_SETTINGS_DISPLAY_PRODUCT_ITEM).map((key, index) => (
                        <Fragment key={index}>
                            <GridItem md={gridColWidth.first}>
                                {FILTER_SETTINGS_DISPLAY_PRODUCT_ITEM[key]}
                            </GridItem>
                            <GridItem md={gridColWidth.second}>
                                <RadioGroup
                                    value={`${settings.settings_display_info_product && settings.settings_display_info_product[key]}`}
                                    onChange={(e) => handleChangeDisplay(e, key)}
                                    row
                                >
                                    <FormControlLabel
                                        value="true"
                                        control={<Radio style={{ padding: '6px' }} color="primary" />}
                                        label={<Typography>表示する</Typography>}
                                    />
                                    <FormControlLabel
                                        value="false"
                                        control={<Radio style={{ padding: '6px' }} color="primary" />}
                                        label={<Typography>表示しない</Typography>}
                                    />
                                </RadioGroup>
                            </GridItem>
                        </Fragment>
                    ))
                }
            </GridContainer>
        </fieldset>
    );
};

SettingBooth.defaultProps = {
    settings: {
        name: '',
        total_media: '',
        image: '',
        video: '',
        audio: '',
        // price_ads: '',
        price: '',
    },
    validateResults: {},
    disabled: false,
    eventInfo: {},
};

SettingBooth.propTypes = {
    title: PropTypes.node.isRequired,
    settings: PropTypes.oneOfType([() => null, PropTypes.object]),
    zone: PropTypes.string.isRequired,
    handleChangeInput: PropTypes.func.isRequired,
    handleChangeRadio: PropTypes.func.isRequired,
    validateResults: PropTypes.oneOfType([() => null, PropTypes.object]),
    disabled: PropTypes.bool,
    eventInfo: PropTypes.objectOf(PropTypes.oneOfType([
        PropTypes.object,
        PropTypes.bool,
        PropTypes.string,
        PropTypes.number,
    ])),
};
const SettingAds = ({
    title, zone, settings, handleChangeInput, validateResults, handleChangeAll, disabled,
}) => {
    const classes = useStyles();
    const intl = useIntl();
    const changeIntInput = (field, value) => {
        if (/^\d*$/.test(value)) {
            if (handleChangeInput) {
                handleChangeInput(zone, field, value ? parseInt(value, 10) : '');
            }
        }
    };
    const changeRegistAds = (value) => {
        let data = {};
        if (value < 1) {
            data = {
                price_ads: 0,
                register_ads: false,
                register_ads_max: 0,
                total_ads: 0,
            };
        } else {
            data = {
                price_ads: 0,
                register_ads: true,
                register_ads_max: 0,
                total_ads: 0,
            };
        }
        handleChangeAll(data);
    };
    const changeRegistAdsMax = (value) => changeIntInput('register_ads_max', value);
    const changePriceAds = (value) => changeIntInput('price_ads', value);
    const changeTotalAds = (value) => changeIntInput('total_ads', value);

    return (

        <fieldset className={classes.booth + (disabled ? ` ${classes.disabled}` : '')} disabled={disabled}>
            <legend>
                <Typography variant="h5" color="primary" bold>広告募集</Typography>
            </legend>
            <GridItem>
                <GridContainer spacing={2}>
                    <GridItem md={3}>
                        <Typography>{title}</Typography>
                    </GridItem>
                    <GridItem md={6} style={{ padding: '0px 8px 8px 8px' }}>
                        <RadioGroup
                            aria-label="register_ads"
                            name="register_ads"
                            value={`${Number(settings.register_ads)}`}
                            onChange={(e) => changeRegistAds(e.target.value)}
                            row
                        >
                            <FormControlLabel
                                value="1"
                                control={<Radio color="primary" />}
                                label={<Typography>する</Typography>}
                            />
                            <FormControlLabel
                                value="0"
                                control={<Radio color="primary" />}
                                label={<Typography>しない</Typography>}
                            />
                        </RadioGroup>
                        <FormHelperText error className="MuiFormHelperText-contained">
                            {validateResults.register_ads
                                ? intl.formatMessage(
                                    { id: validateResults.register_ads.message },
                                ) : ''}
                        </FormHelperText>
                    </GridItem>
                </GridContainer>

                <GridContainer spacing={2}>
                    <GridItem md={3}>
                        <Typography>広告募集（1社あたり限定）件数</Typography>
                    </GridItem>
                    <GridItem md={6}>
                        <TextField
                            variant="outlined"
                            type="number"
                            className={classes.input}
                            value={settings.register_ads_max}
                            onChange={(e) => changeRegistAdsMax(e.target.value)}
                            inputProps={{
                                min: 0,
                            }}
                            InputProps={{ classes: { root: classes.numberInput } }}
                            disabled={!(settings.register_ads > 0)}
                            error={!!validateResults.register_ads_max}
                            helperText={validateResults.register_ads_max
                                ? intl.formatMessage(
                                    { id: validateResults.register_ads_max.message },
                                ) : ''}
                        />
                    </GridItem>
                </GridContainer>

                <GridContainer spacing={2}>
                    <GridItem md={3}>
                        <Typography>広告料金</Typography>
                    </GridItem>
                    <GridItem md={6}>
                        <TextField
                            variant="outlined"
                            className={classes.input}
                            value={settings.price_ads}
                            onChange={(e) => changePriceAds(e.target.value)}
                            inputProps={{
                                min: 0,
                            }}
                            InputProps={{ classes: { root: `${classes.numberInput} ${classes.price}` } }}
                            disabled={!(settings.register_ads > 0)}
                            error={!!validateResults.price_ads}
                            helperText={validateResults.price_ads
                                ? intl.formatMessage(
                                    { id: validateResults.price_ads.message },
                                ) : ''}
                        />
                    </GridItem>
                </GridContainer>

                <GridContainer spacing={2}>
                    <GridItem md={3}>
                        <Typography>広告総募集件数</Typography>
                    </GridItem>
                    <GridItem md={6}>
                        <TextField
                            variant="outlined"
                            type="number"
                            className={classes.input}
                            value={settings.total_ads}
                            onChange={(e) => changeTotalAds(e.target.value)}
                            // onKeyPress={handleKeyPressInput}
                            inputProps={{
                                min: 0,
                            }}
                            InputProps={{ classes: { root: classes.numberInput } }}
                            disabled={!(settings.register_ads > 0)}
                            error={!!validateResults.total_ads}
                            helperText={validateResults.total_ads
                                ? intl.formatMessage(
                                    { id: validateResults.total_ads.message },
                                ) : ''}
                        />
                    </GridItem>
                </GridContainer>

            </GridItem>
        </fieldset>
    );
};

SettingAds.defaultProps = {
    settings: {
    },
    validateResults: {},
    disabled: false,
};

SettingAds.propTypes = {
    title: PropTypes.node.isRequired,
    settings: PropTypes.oneOfType([() => null, PropTypes.object]),
    zone: PropTypes.string.isRequired,
    handleChangeInput: PropTypes.func.isRequired,
    handleChangeAll: PropTypes.func.isRequired,
    validateResults: PropTypes.oneOfType([() => null, PropTypes.object]),
    disabled: PropTypes.bool,
};

SettingBoothPage.defaultProps = {
    settingBoothData: {},
    errors: {},
    getSettingBooth: undefined,
    updateSettingBooth: undefined,
    getSettingBoothClear: undefined,
    updateSettingBoothClear: undefined,
    isSuccessful: undefined,
    eventInfo: {},
    fetchInfoEvent: () => {},
};

SettingBoothPage.propTypes = {
    settingBoothData: PropTypes.oneOfType([() => null, PropTypes.object]),
    errors: PropTypes.oneOfType([() => null, PropTypes.object]),
    isCompleted: PropTypes.bool.isRequired,
    getSettingBooth: PropTypes.func,
    updateSettingBooth: PropTypes.func,
    getSettingBoothClear: PropTypes.func,
    updateSettingBoothClear: PropTypes.func,
    isCompletedGet: PropTypes.bool.isRequired,
    isLoading: PropTypes.bool.isRequired,
    isSuccessful: PropTypes.bool,
    eventInfo: PropTypes.objectOf(PropTypes.oneOfType([
        PropTypes.object,
        PropTypes.bool,
        PropTypes.string,
        PropTypes.number,
    ])),
    fetchInfoEvent: PropTypes.func,
};

export default SettingBoothPage;
